import { Eye,Sparkles, Star } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ITool } from '@/interfaces/ITool';
import { isVisualizer } from '@/interfaces/ITool';

export interface ToolRowProps {
  tool: ITool;
  selected: boolean;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  isRecommended: boolean;
  recommendedReason?: string;
  onSelect: () => void;
  onAddToPipeline: () => void;
}

export const ToolRow: React.FC<ToolRowProps> = ({
  tool,
  selected,
  isFavorite,
  onToggleFavorite,
  isRecommended,
  recommendedReason,
  onSelect,
  onAddToPipeline,
}) => {
  const { t } = useTranslation('sidebar');
  const isViz = isVisualizer(tool);

  return (
    <div
      className={
        'group flex items-center gap-2 rounded-md px-2 py-2 border border-transparent ' +
        (selected
          ? 'bg-accent text-accent-foreground'
          : isRecommended
            ? 'bg-primary/10 border border-primary/30 hover:bg-primary/20'
            : 'hover:bg-accent/60')
      }
    >
      <button
        onClick={onSelect}
        className="flex-1 min-w-0 text-left"
        title={isRecommended ? recommendedReason : tool.description}
      >
        <div className="text-sm font-medium truncate flex items-center gap-1">
          {isViz && <Eye className="h-3 w-3 shrink-0 text-purple-500" />}
          {tool.name}
          {isRecommended && (
            <Sparkles className="h-3.5 w-3.5 text-primary" />
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {(tool.category ?? 'Uncategorized').toString()}
        </div>
      </button>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 px-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorite();
              }}
              aria-label={isFavorite ? t('tools.unfavorite') : t('tools.favorite')}
            >
              <Star
                className={
                  'h-4 w-4 ' +
                  (isFavorite ? 'text-primary fill-current' : 'text-muted-foreground')
                }
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{isFavorite ? t('tools.unfavorite') : t('tools.favorite')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={selected ? 'ghost' : 'secondary'}
              size="sm"
              className="h-7 px-2"
              onClick={onAddToPipeline}
              aria-label={t('tools.addToPipeline')}
            >
              +
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('tools.addToPipeline')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
